import { useCallback, useMemo } from 'react'
import { useLayoutStore, useLayoutStoreApi } from '@/workspace/layout-store/hooks'
import type { PanelState, TemplateData } from './types'

/**
 * Per-panel state consumer hook.
 * Subscribes via Zustand selector — only re-renders when this panel changes.
 *
 * `loaded` is derived, never written: hydration marks every panel that had saved
 * state, so once the layout document is in (`layoutLoaded`) an absent entry means
 * "no saved state" and the panel may fetch. Deriving it makes that true on the
 * first render instead of one commit later.
 *
 * Generic parameter T allows feature-specific typing:
 *   const { panelState, updatePanel } = useLayoutPanel<ChartPanelState>(id)
 */
export function useLayoutPanel<T extends PanelState = PanelState>(panelId: string) {
  const store = useLayoutStoreApi()
  const panelState = useLayoutStore((s) => s.panels[panelId] ?? null) as T | null
  const loaded = useLayoutStore((s) => s.panelLoaded[panelId] ?? s.layoutLoaded)
  const templateKey = useLayoutStore((s) => s.templateKeys[panelId] ?? 0)

  const updatePanel = useCallback(
    (partial: Partial<T>) => store.getState().updatePanel(panelId, partial as Partial<PanelState>),
    [store, panelId],
  )

  const loadTemplate = useCallback(
    (tpl: TemplateData) => store.getState().loadTemplate(panelId, tpl),
    [store, panelId],
  )

  return useMemo(
    () => ({ panelState, loaded, updatePanel, templateKey, loadTemplate }),
    [panelState, loaded, updatePanel, templateKey, loadTemplate],
  )
}
